import type { Mods } from '../sim/stats';
import { P } from '../world/player';
/* ============================================================
   機體武裝強化（Boss 擊破獎勵，2026-08-03）
   ============================================================

   機體給的三把（主武裝 + 小招 + 大招）原本是**沒有等級的固定身分**
   （GDD §7.1、`sim/loadout.ts` 檔頭）。那個決定解的是「池子若等於機體武裝，
   這一場抽到什麼的驚喜只剩三次」—— 那個理由到現在仍然成立，所以這一層
   **不進升級池**，它走一條獨立的來源：**打贏 Boss**（外加精英的小機率掉落）。

   ## 使用者拍板的五件事（2026-08-03，不要重新詮釋）

     1. 三把武器 × **兩種走向 = 六種**（第一版只有三種，使用者要更多變化）
     2. **不佔武裝槽**：機體三把是身分，5 格仍然全部留給池子武裝
     3. 主要來源是 Boss（一場四隻），**精英首領有少量機率掉落**
     4. 觸發是**立刻跳出三選一**（精英那條是掉在地上的箱子，走過去撿）
     5. 發牌規則：**每把武器保證一張**，兩種走向之間隨機挑一張
        —— 玩家永遠選得到「要餵哪一把」，但拿到的是威力還是節奏會變

   ## 六種怎麼分的：每把武器各有「威力」與「節奏」兩條

     威力線   把單次輸出堆高 —— 打硬目標、Boss、精英
     節奏線   把「多久能再來一次」壓短 —— 清場、續戰、更多命中事件

   兩條線在同一把武器上是**真的取捨**：威力線的過量傷害會浪費在雜魚身上，
   節奏線面對高血量目標則是慢慢磨。這一組對比就是這一層唯一的分歧
   ——刻意沒有規則類效果，也沒有質變門檻。

   ## 為什麼只是數值

   使用者的原話：「升級是數值上的提升 不會是玩法上的改變（這對應到的是未來換機型）」。
   玩法的分歧由池子武裝、進化與（未來的）機型負責，這裡只回答「我這台打得動嗎」。

   ## 幅度怎麼定的

   一場的次數很少（四隻 Boss ＋ 精英的零星掉落），每一級都必須**當場有感** ——
   否則「打贏 Boss 拿到的東西」會比一次普通升級還沒感覺，那比不給更糟。
   所以單軸卡每級 +24~26%（一般被動的 `p-power` 是 +12%/級）。
   單軸滿級（4 級）約 ×2.4；而六種攤下來，實務上一條線疊到 2–3 級就很多了。 */

export type MechUp = {
  id: string;
  /** 對應機體的哪一把（`P.mech.primary` / `mid` / `ult`）。 */
  slot: 'primary' | 'mid' | 'ult';
  /** 這一條線的名字（卡片標題是「武器名 · 這個」）。 */
  n: string;
  kind: string;
  axes: string[];
  /** 卡片圖示的 key。 */
  ico: string;
  d: (lv: number) => string;
  mods: (lv: number) => Mods;
  /** 上限 4 級：四隻 Boss 全押同一條線就是它。 */
  max?: number;
};

function pct(mul, lv) { return Math.round((Math.pow(mul, lv) - 1) * 100); }
function cut(mul, lv) { return Math.round((1 - Math.pow(mul, lv)) * 100); }

export var SLOT_CN = { primary: '主武裝', mid: '小招', ult: '大招' };
export var SLOTS: Array<'primary' | 'mid' | 'ult'> = ['primary', 'mid', 'ult'];

/** 這一條線在當前機體上叫什麼（「機炮 · 彈頭增壓」）。
    **武器名跟著機體走**而不是寫死：LANCER-2 的卡片要寫「電磁刃」，
    寫死「機炮」的話玩家會以為自己拿錯東西。 */
export function mechUpName(u: MechUp) {
  var i = u.slot === 'primary' ? 0 : (u.slot === 'mid' ? 1 : 2);
  var w = P.mech && P.mech.icons ? P.mech.icons[i] : null;
  return (w || SLOT_CN[u.slot]) + ' · ' + u.n;
}

export var MECH_UPGRADES: MechUp[] = [
  /* ── 主武裝：唯一全程自動開火的輸出 ─────────────────────────── */
  {
    id: 'mw-atk-dmg', slot: 'primary', n: '彈頭增壓', kind: '機體武裝', axes: ['傷害'], ico: 'ax-傷害',
    d: function (lv) { return '主武裝傷害 +' + pct(1.24, lv) + '%'; },
    mods: function (lv) { return { mul: { atkDmg: Math.pow(1.24, lv) } }; }
  },
  {
    /* 射速線比傷害線多給一點百分比：同樣的 DPS 之下，射速的價值有一部分
       會被「過量傷害」以外的東西吃掉（切換目標、彈道飛行時間），
       而它換來的是更密的命中事件（衝擊累積、暴擊機會）。 */
    id: 'mw-atk-rate', slot: 'primary', n: '高速供彈', kind: '機體武裝', axes: ['射速'], ico: 'ax-射速',
    d: function (lv) { return '主武裝射速 +' + pct(1.14, lv) + '%'; },
    mods: function (lv) { return { mul: { atkRate: Math.pow(1.14, lv) } }; }
  },

  /* ── 小招（J）：手動的爆發 ──────────────────────────────────── */
  {
    id: 'mw-mid-dmg', slot: 'mid', n: '高爆彈頭', kind: '機體武裝', axes: ['傷害'], ico: 'ax-傷害',
    d: function (lv) { return '小招傷害 +' + pct(1.26, lv) + '%'; },
    mods: function (lv) { return { mul: { midDmg: Math.pow(1.26, lv) } }; }
  },
  {
    /* **乘算不是減法。** 減法型的冷卻縮減疊下去會變成沒有冷卻，
       那是這類詞條最典型的死法（`p-armor` 的註解記過同一個坑）。 */
    id: 'mw-mid-cd', slot: 'mid', n: '快速裝填', kind: '機體武裝', axes: ['冷卻'], ico: 'ax-冷卻',
    d: function (lv) { return '小招冷卻 −' + cut(0.89, lv) + '%'; },
    mods: function (lv) { return { mul: { midCdMax: Math.pow(0.89, lv) } }; }
  },

  /* ── 大招（K）：一場裡最貴的一次按鍵 ────────────────────────── */
  {
    id: 'mw-ult-dmg', slot: 'ult', n: '聚焦透鏡', kind: '機體武裝', axes: ['傷害'], ico: 'ax-傷害',
    d: function (lv) { return '大招傷害 +' + pct(1.26, lv) + '%'; },
    mods: function (lv) { return { mul: { ultDmg: Math.pow(1.26, lv) } }; }
  },
  {
    /* 持續時間對大招是**傷害以外的另一種倍率**：光束與旋刃的總傷害由
       「掃過幾次」決定（GDD §6.2「大招的傷害由玩家的站位決定」），
       所以拉長持續等於把那個站位決定放大 —— 跟直接加傷害不是同一件事。 */
    id: 'mw-ult-dur', slot: 'ult', n: '延長迴路', kind: '機體武裝', axes: ['持續', '冷卻'], ico: 'ax-持續',
    d: function (lv) { return '大招持續時間 +' + pct(1.16, lv) + '%、冷卻 −' + cut(0.93, lv) + '%'; },
    mods: function (lv) { return { mul: { ultDur: Math.pow(1.16, lv), ultCdMax: Math.pow(0.93, lv) } }; }
  }
];

export var MECH_UP_MAX = 4;
/** 精英**首領**擊破時掉落武裝強化箱的機率（使用者：「少量機率」）。
    精英約每 60 秒一組、一組只有首領會掉東西，20 分鐘約 19 次 ——
    12% 大概等於一場多 2 次強化，跟四隻 Boss 的 4 次相比仍然是配角。 */
export var MW_DROP_CHANCE = 0.12;

export function mechUpMax(u) { return (u && u.max) || MECH_UP_MAX; }
export function mechUpById(id): MechUp | null {
  for (var i = 0; i < MECH_UPGRADES.length; i++) if (MECH_UPGRADES[i].id === id) return MECH_UPGRADES[i];
  return null;
}
